import { SurfaceBadge } from '@/components/match/surface-badge';
import {
  formatStartsAt,
  levelChipClass,
  levelFullName,
  marketLabel,
  shortLevel,
} from '@/components/opportunity/card-formatters';
import { StakeChip } from '@/components/opportunity/stake-chip';
import { StateBadge } from '@/components/opportunity/state-badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { edgeColorClass, formatEdge } from '@/lib/format-edge';
import { formatOdds } from '@/lib/format-odds';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { cn } from '@/lib/utils';
import type { OpportunityRow, TrackedSignalBookOdds } from '@tennis-scanner/shared';
import { Calendar, Check, MoonStar, X } from 'lucide-react';

// Sub-components for OpportunityCard. Aggressively compressed (architecture
// § 21.4.1) — target ≤ 95px per card so 8-10 alerts fit per 1080p screen.
// Whitespace minimised; key data points (edge, best odds, stake) get the
// largest fonts.

// ─── Row 1 — context strip (tags · date) ─────────────────────────────────────
export function ContextRow({
  opportunity,
  tournamentDisplay,
  dataObservedAt,
}: {
  opportunity: OpportunityRow;
  tournamentDisplay: string;
  dataObservedAt: string | null;
}) {
  const match = opportunity.match;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px] font-mono uppercase tracking-wider leading-none text-muted-foreground">
      <StateBadge state={opportunity.state} />
      {match?.tournamentLevel && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn('rounded-sm border px-1 py-0', levelChipClass(match.tournamentLevel))}
            >
              {shortLevel(match.tournamentLevel)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{levelFullName(match.tournamentLevel)}</TooltipContent>
        </Tooltip>
      )}
      <SurfaceBadge surface={match?.surface ?? null} />
      <span className="truncate text-foreground">{tournamentDisplay}</span>
      {match?.bestOf && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>BO{match.bestOf}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Mejor de {match.bestOf} sets (best-of-{match.bestOf})
          </TooltipContent>
        </Tooltip>
      )}
      {match?.status && match.status !== 'scheduled' && <span>{match.status}</span>}
      {opportunity.rankScore !== null && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>prio {opportunity.rankScore.toFixed(2)}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            Prioridad — orden en la lista. Combina edge, frescura de la cuota y cuánto falta para
            que empiece. Mayor = más urgente / valioso.
          </TooltipContent>
        </Tooltip>
      )}
      <FreshnessChip observedAt={dataObservedAt} />
      <span className="ml-auto inline-flex items-center gap-1 text-[10px] normal-case tracking-normal text-foreground">
        <Calendar className="size-3" />
        {formatStartsAt(match?.startsAt ?? null)}
      </span>
    </div>
  );
}

// ─── Row 2 — match · edge (BIG) · best odds (BIG) · best book ───────────────
export function HeadlineRow({
  opportunity,
  matchTitle,
  sel,
}: {
  opportunity: OpportunityRow;
  matchTitle: string;
  sel: string;
}) {
  return (
    <div className="mt-0.5 flex items-baseline gap-2 leading-tight">
      <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
        {matchTitle}
      </h3>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'shrink-0 font-mono text-2xl font-bold leading-none',
              edgeColorClass(opportunity.currentEdgePct),
            )}
          >
            {formatEdge(opportunity.currentEdgePct)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">
          Edge = (mejor cuota actual ÷ cuota mínima) − 1.
          <br />
          Mejor: {formatOdds(opportunity.currentBestOdds)} · target{' '}
          {formatOdds(opportunity.targetOdds)}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex shrink-0 items-baseline gap-1 rounded-md border border-positive/40 bg-positive/10 px-1.5 py-0.5 leading-none">
            <span className="font-mono text-lg font-bold text-positive">
              {formatOdds(opportunity.currentBestOdds)}
            </span>
            <span className="text-[10px] font-medium text-foreground">
              {opportunity.currentBestBook ?? '—'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="left">
          Cuota más alta entre las casas operables para {sel}.
          <br />
          Detectada {formatRelativeTime(opportunity.firstDetectedAt)}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// ─── Row 3 — pick details · stake · actions ─────────────────────────────────
interface PickRowProps {
  opportunity: OpportunityRow;
  sel: string;
  isTerminal: boolean;
  onExecuteClick: (opp: OpportunityRow) => void;
  onSnooze: () => void;
  onPass: () => void;
  snoozePending: boolean;
  passPending: boolean;
}

export function PickRow(p: PickRowProps) {
  return (
    <div
      className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 leading-tight"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="toolbar"
    >
      <span className="rounded-sm border border-border bg-muted px-1 py-0 font-mono text-[10px] uppercase tracking-wider leading-none text-muted-foreground">
        {marketLabel(p.opportunity.market)}
      </span>
      <span className="text-xs font-medium text-foreground">{p.sel}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="font-mono text-[11px] text-muted-foreground">
            tgt{' '}
            <span className="text-sm font-bold text-foreground">
              {formatOdds(p.opportunity.targetOdds)}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Cuota mínima cargada en el pick. Si una casa paga ≥ esto, se dispara la alerta.
        </TooltipContent>
      </Tooltip>
      <StakeChip stake={p.opportunity.stake} manualStakeUsd={p.opportunity.manualStakeUsd} />
      <div className="ml-auto flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5"
              disabled={p.isTerminal || p.snoozePending}
              onClick={p.onSnooze}
            >
              <MoonStar className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Pausar 1h (atajo S)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5"
              disabled={p.isTerminal || p.passPending}
              onClick={p.onPass}
            >
              <X className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Descartar (atajo P)</TooltipContent>
        </Tooltip>
        <Button
          size="sm"
          className="h-6 px-2"
          disabled={p.isTerminal}
          onClick={() => p.onExecuteClick(p.opportunity)}
        >
          <Check className="size-3.5" />
          Ejecutar
        </Button>
      </div>
    </div>
  );
}

// ─── Freshness chip — green/amber/red by data age ───────────────────────────
function FreshnessChip({ observedAt }: { observedAt: string | null | undefined }) {
  if (!observedAt) return null;
  const ageSec = Math.max(0, Math.floor((Date.now() - new Date(observedAt).getTime()) / 1000));
  let cls = 'bg-positive/10 text-positive border-positive/30';
  let label = `${ageSec}s`;
  if (ageSec > 300) {
    cls = 'bg-destructive/10 text-destructive border-destructive/30';
    label = `${Math.floor(ageSec / 60)}m`;
  } else if (ageSec > 90) {
    cls = 'bg-warning/10 text-warning border-warning/30';
    label = ageSec >= 60 ? `${Math.floor(ageSec / 60)}m` : `${ageSec}s`;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[10px] font-mono leading-none',
            cls,
          )}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        Edad de la cuota más fresca del feed.
        <br />
        ≤30s verde · ≤90s ámbar · &gt;90s rojo
      </TooltipContent>
    </Tooltip>
  );
}

// Pulls the youngest observedAt from a perBook array.
export function freshestPerBook(perBook: TrackedSignalBookOdds[]): string | null {
  let best: string | null = null;
  let bestT = 0;
  for (const b of perBook) {
    const t = Date.parse(b.observedAt);
    if (Number.isFinite(t) && t > bestT) {
      bestT = t;
      best = b.observedAt;
    }
  }
  return best;
}
